import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import TopNav from "../../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

const fieldStyle = {
  width: "100%",
  padding: "0.75rem",
  borderRadius: "8px",
  border: "1px solid #ddd",
  fontSize: "1rem",
};

const mapFrameStyle = {
  width: "100%",
  height: "420px",
  border: 0,
  borderRadius: "8px",
};

export default function UserWhereIsMyBus() {
  const { t } = useTranslation();

  // ---------------------------------------------------------
  // HIERARCHY
  // ---------------------------------------------------------

  const [vendors, setVendors] = useState([]);
  const [buses, setBuses] = useState([]);

  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedBusId, setSelectedBusId] = useState("");

  // ---------------------------------------------------------
  // LOCATION
  // ---------------------------------------------------------

  const [location, setLocation] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  // ---------------------------------------------------------
  // LOADING / ERROR
  // ---------------------------------------------------------

  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // FETCH ACCESSIBLE VENDORS
  // =========================================================

  const fetchVendors = async () => {
    setLoadingVendors(true);
    setError("");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/vendors`
      );

      const vendorList = Array.isArray(res.data)
        ? res.data
        : [];

      setVendors(vendorList);

      // Exactly one accessible vendor:
      // automatically select it.
      if (vendorList.length === 1) {
        setSelectedVendorId(
          String(vendorList[0].vendor_id)
        );
      } else {
        setSelectedVendorId("");
      }
    } catch (err) {
      console.error(
        "Failed to fetch accessible vendors",
        err
      );

      setVendors([]);
      setSelectedVendorId("");

      setError(
        t(
          "user_where_is_my_bus.messages.could_not_load_vendors"
        )
      );
    } finally {
      setLoadingVendors(false);
    }
  };

  // =========================================================
  // FETCH ACCESSIBLE BUSES FOR VENDOR
  // =========================================================

  const fetchBuses = async (vendorId) => {
    if (!vendorId) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    setLoadingBuses(true);
    setError("");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/buses`,
        {
          params: {
            vendor_id: vendorId,
          },
        }
      );

      const busList = Array.isArray(res.data)
        ? res.data
        : [];

      setBuses(busList);

      // Clear any bus belonging to the previously selected vendor.
      setSelectedBusId("");

      // Exactly one accessible bus:
      // automatically select it.
      if (busList.length === 1) {
        setSelectedBusId(
          String(busList[0].bus_id)
        );
      }
    } catch (err) {
      console.error(
        "Failed to fetch accessible buses",
        err
      );

      setBuses([]);
      setSelectedBusId("");

      setError(
        t(
          "user_where_is_my_bus.messages.could_not_load_buses"
        )
      );
    } finally {
      setLoadingBuses(false);
    }
  };

  // =========================================================
  // FETCH BUS LOCATION
  // =========================================================

  const fetchBusLocation = async () => {
    if (!selectedBusId) {
      setError(
        t(
          "user_where_is_my_bus.messages.select_bus_first"
        )
      );
      return;
    }

    setLoadingLocation(true);
    setError("");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/location/buses/${selectedBusId}`
      );

      setLocation(res.data);
      setLastRefreshAt(new Date());
    } catch (err) {
      console.error(
        "Failed to fetch bus location",
        err
      );

      setError(
        t(
          "user_where_is_my_bus.messages.could_not_fetch_location"
        )
      );
    } finally {
      setLoadingLocation(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${localStorage.getItem("token")}`;

    fetchVendors();
  }, []);

  // =========================================================
  // VENDOR CHANGED
  // =========================================================

  useEffect(() => {
    // Never leave the previous bus location visible after
    // changing vendor.
    setLocation(null);
    setLastRefreshAt(null);

    if (selectedVendorId) {
      fetchBuses(selectedVendorId);
    } else {
      setBuses([]);
      setSelectedBusId("");
    }
  }, [selectedVendorId]);

  // =========================================================
  // EVENT HANDLERS
  // =========================================================

  const handleVendorChange = (e) => {
    const vendorId = e.target.value;

    setSelectedVendorId(vendorId);
    setSelectedBusId("");

    setLocation(null);
    setLastRefreshAt(null);
    setError("");
  };

  const handleBusChange = (e) => {
    setSelectedBusId(e.target.value);

    setLocation(null);
    setLastRefreshAt(null);
    setError("");
  };

  // =========================================================
  // DROPDOWN RULES
  // =========================================================

  const vendorDropdownDisabled =
    loadingVendors ||
    vendors.length <= 1;

  const busDropdownDisabled =
    loadingBuses ||
    !selectedVendorId ||
    buses.length <= 1;

  // =========================================================
  // MAP
  // =========================================================

  const hasCoordinates =
    location?.lat != null &&
    location?.lng != null;

  const mapUrl = hasCoordinates
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${
        location.lng - 0.01
      }%2C${location.lat - 0.01}%2C${
        location.lng + 0.01
      }%2C${
        location.lat + 0.01
      }&layer=mapnik&marker=${
        location.lat
      }%2C${location.lng}`
    : null;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <StandardScreen
      topNav={
        <TopNav
          title={t(
            "user_where_is_my_bus.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            <Card
              title={t(
                "user_where_is_my_bus.title"
              )}
            >
              <Stack gap="3">

                <p style={{ margin: 0 }}>
                  {t(
                    "user_where_is_my_bus.subtitle"
                  )}
                </p>

                {error && (
                  <div style={{ color: "red" }}>
                    {error}
                  </div>
                )}

                {/* -----------------------------------------
                    VENDOR
                ------------------------------------------ */}

                <div>
                  <label>
                    {t(
                      "user_where_is_my_bus.form.vendor"
                    )}
                  </label>

                  <select
                    value={selectedVendorId}
                    onChange={handleVendorChange}
                    disabled={vendorDropdownDisabled}
                    style={fieldStyle}
                  >
                    <option value="">
                      {loadingVendors
                        ? t(
                            "user_where_is_my_bus.messages.loading_vendors"
                          )
                        : t(
                            "user_where_is_my_bus.form.select_vendor"
                          )}
                    </option>

                    {vendors.map((vendor) => (
                      <option
                        key={vendor.vendor_id}
                        value={vendor.vendor_id}
                      >
                        {vendor.vendor_id} |{" "}
                        {vendor.vendor_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* -----------------------------------------
                    BUS
                ------------------------------------------ */}

                <div>
                  <label>
                    {t(
                      "user_where_is_my_bus.form.bus_route"
                    )}
                  </label>

                  <select
                    value={selectedBusId}
                    onChange={handleBusChange}
                    disabled={busDropdownDisabled}
                    style={fieldStyle}
                  >
                    <option value="">
                      {loadingBuses
                        ? t(
                            "user_where_is_my_bus.messages.loading_buses"
                          )
                        : t(
                            "user_where_is_my_bus.form.select_bus"
                          )}
                    </option>

                    {buses.map((bus) => (
                      <option
                        key={bus.bus_id}
                        value={bus.bus_id}
                      >
                        {bus.bus_id} |{" "}
                        {bus.plate_number}
                      </option>
                    ))}
                  </select>
                </div>

                {/* -----------------------------------------
                    LOCATION BUTTON
                ------------------------------------------ */}

                <Button
                  fullWidth
                  onClick={fetchBusLocation}
                  disabled={
                    loadingLocation ||
                    !selectedBusId
                  }
                >
                  {loadingLocation
                    ? t(
                        "user_where_is_my_bus.buttons.checking"
                      )
                    : t(
                        "user_where_is_my_bus.buttons.where_is_it"
                      )}
                </Button>

              </Stack>
            </Card>

            {/* =============================================
                LOCATION CARD
            ============================================== */}

            {location && (
              <Card
                title={t(
                  "user_where_is_my_bus.location_card.title"
                )}
              >
                <Stack gap="3">

                  <p style={{ margin: 0 }}>
                    <strong>
                      {t(
                        "user_where_is_my_bus.location_card.plate"
                      )}
                      :
                    </strong>{" "}
                    {location.plate}
                  </p>

                  <p style={{ margin: 0 }}>
                    <strong>
                      {t(
                        "user_where_is_my_bus.location_card.speed"
                      )}
                      :
                    </strong>{" "}
                    {location.speed} km/h
                  </p>

                  <p style={{ margin: 0 }}>
                    <strong>
                      {t(
                        "user_where_is_my_bus.location_card.address"
                      )}
                      :
                    </strong>{" "}
                    {location.address || "-"}
                  </p>

                  {location?.GPSlastTime && (
                    <p style={{ margin: 0 }}>
                      <strong>
                        {t(
                          "user_where_is_my_bus.location_card.gps_latest_timestamp"
                        )}
                        :
                      </strong>{" "}
                      <strong>
                        {location.GPSlastTime}
                      </strong>
                    </p>
                  )}

                  {lastRefreshAt && (
                    <p style={{ margin: 0 }}>
                      <strong>
                        {t(
                          "user_where_is_my_bus.location_card.ui_latest_timestamp"
                        )}
                        :
                      </strong>{" "}
                      <strong>
                        {lastRefreshAt.toLocaleTimeString()}
                      </strong>
                    </p>
                  )}

                  {mapUrl && (
                    <iframe
                      title={t(
                        "user_where_is_my_bus.location_card.map_title"
                      )}
                      src={mapUrl}
                      style={mapFrameStyle}
                    />
                  )}

                </Stack>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}